import {BranchMarket, DELIVERY_MEMO_ERROR_MESSAGE, MaxLength} from '@web/core/constants'

/**
 * order-manager 주문배송지 검증 api 응답 enum
 * @see http://alpha-api.order-manager.pay.navercorp.com/swagger-ui/index.html#/orderDeliveryAddress/validateOrderDeliveryAddressByBaseAddress
 * */
export const VALIDATION_FAIL = {
    INTERNAL_ERROR: 'INTERNAL_ERROR',
    INVALID_ZIPCODE: 'INVALID_ZIPCODE',
    NOT_FOUND_ADDRESS_WITH_FIVE_DIGITS_ZIPCODE: 'NOT_FOUND_ADDRESS_WITH_FIVE_DIGITS_ZIPCODE',
    NOT_FOUND_ADDRESS_WITH_SIX_DIGITS_ZIPCODE: 'NOT_FOUND_ADDRESS_WITH_SIX_DIGITS_ZIPCODE',
    MISMATCH_ZIPCODE_WITH_ROAD_ADDRESS_AND_FIVE_DIGITS_ZIPCODE:
        'MISMATCH_ZIPCODE_WITH_ROAD_ADDRESS_AND_FIVE_DIGITS_ZIPCODE',
    MISMATCH_ZIPCODE_WITH_ROAD_ADDRESS_AND_SIX_DIGITS_ZIPCODE:
        'MISMATCH_ZIPCODE_WITH_ROAD_ADDRESS_AND_SIX_DIGITS_ZIPCODE',
    MISMATCH_ZIPCODE_WITH_JIBUN_ADDRESS_AND_FIVE_DIGITS_ZIPCODE:
        'MISMATCH_ZIPCODE_WITH_JIBUN_ADDRESS_AND_FIVE_DIGITS_ZIPCODE',
    MISMATCH_ZIPCODE_WITH_JIBUN_ADDRESS_AND_SIX_DIGITS_ZIPCODE:
        'MISMATCH_ZIPCODE_WITH_JIBUN_ADDRESS_AND_SIX_DIGITS_ZIPCODE',
} as const

export type ValidationFailType = Constant<typeof VALIDATION_FAIL>

/** order-manager 주문배송지 검증 api 응답 결과 배송지변경 배너가 노출되어야 하는 케이스 */
export const suggestBannerShowNeededFailType = [
    VALIDATION_FAIL.NOT_FOUND_ADDRESS_WITH_FIVE_DIGITS_ZIPCODE,
    VALIDATION_FAIL.MISMATCH_ZIPCODE_WITH_JIBUN_ADDRESS_AND_FIVE_DIGITS_ZIPCODE,
    VALIDATION_FAIL.MISMATCH_ZIPCODE_WITH_ROAD_ADDRESS_AND_FIVE_DIGITS_ZIPCODE,
]

/**
 * suggestBannerShowNeededFailType 으로 배송지변경 배너가 노출될 때
 * 해당 배너를 통해 배송지변경창 진입 시 주소검색어가 입력된 채로 노출되어야 하는 케이스
 * */
export const fillBaseAddressNeededFailType = [
    VALIDATION_FAIL.MISMATCH_ZIPCODE_WITH_JIBUN_ADDRESS_AND_FIVE_DIGITS_ZIPCODE,
    VALIDATION_FAIL.MISMATCH_ZIPCODE_WITH_ROAD_ADDRESS_AND_FIVE_DIGITS_ZIPCODE,
]

/**
 * 정기구독 신청서에서 order-manager 주문배송지 검증 api로 배송지검증 여부를 제어하는 enum.
 * 정기구독 신청서 초기화 시, 배송지 변경 시 검증.
 * 주문서에서는 초기화 시, 배송지 변경 시점을 파악할 때 이 enum을 사용하지 않음.
 * */
export const ZipCodeValidationStatus = {
    IDLE: 'IDLE',
    READY: 'READY',
} as const

/** FE 검증 성공 코드 */
export const VALIDATION_SUCCESS = 'SUCCESS' as const

/**
 * 기본 배송지 FE 검증 에러 코드
 * */
const BaseAddressValidationErrorCode = {
    /** 배송지 입력 실패 */
    DELIVERY_INPUT_FAIL: 'DELIVERY_INPUT_FAIL',
    /** 배송지 수령인 입력 실패 */
    DELIVERY_RECEIVER_NAME_FAIL: 'DELIVERY_RECEIVER_NAME',
    /** 배송지 수령인 입력 초과 실패 */
    DELIVERY_RECEIVER_NAME_MAX_FAIL: 'DELIVERY_RECEIVER_NAME_MAX_FAIL',
    /** 배송지 상세 입력 초과 실패 */
    DELIVERY_DETAIL_ADDRESS_MAX_FAIL: 'DELIVERY_DETAIL_ADDRESS_MAX_FAIL',
    /** 배송지 명 입력 초과 실패 */
    DELIVERY_ADDRESS_NAME_MAX_FAIL: 'DELIVERY_ADDRESS_NAME_MAX_FAIL',
    /** 배송지 휴대폰 번호1 입력 실패 */
    DELIVERY_PHONE_NUMBER_FAIL: 'DELIVERY_PHONE_NUMBER_FAIL',
    /** 배송지 휴대폰 번호2 입력 실패 */
    DELIVERY_PHONE_NUMBER_TWO_FAIL: 'DELIVERY_PHONE_NUMBER_TWO_FAIL',
    /** 배송지 한국어 입력 실패 */
    DELIVERY_KOREA_ADDRESS_FAIL: 'DELIVERY_KOREA_ADDRESS_FAIL',
    /** 배송지 상세 주소 입력 실패 (도로명) */
    DELIVERY_ROAD_DETAIL_NONE: 'DELIVERY_ROAD_DETAIL_NONE',
    /** 배송지 상세 주소 입력 실패 (지번) */
    DELIVERY_LOT_DETAIL_NONE: 'DELIVERY_LOT_DETAIL_NONE',
    /** 폐지된 우편번호 사용 */
    DEPRECATED_ZIPCODES: 'DEPRECATED_ZIPCODES',
    /** 해외직배송상품은 통관진행 및 원활한 배송처리를 위해 도로명주소+5자리 우편번호로 입력하셔야 합니다.\n배송지 주소를 도로명 주소로 수정해주세요. */
    OVERSEA_DELIVERY_NOT_ROAD_ADDRESS: 'OVERSEA_DELIVERY_NOT_ROAD_ADDRESS',
    /** 도착보장은 우편번호가 5자리일때 만 가능 */
    REJECT_SIX_DIGITS_ZIPCODE_WITH_ARRIVAL_GUARANTEE_PRODUCT:
        'REJECT_SIX_DIGITS_ZIPCODE_WITH_ARRIVAL_GUARANTEE_PRODUCT',
    /** 6자리 우편번호 */
    REJECT_LOT_NUMBER_ADDRESS: 'REJECT_LOT_NUMBER_ADDRESS',
} as const

/**
 * 기본 배송지 FE 검증 경고 코드
 * */
const BaseAddressValidationWarningCode = {
    /** 도로명주소이면서 상세주소 없는 경우 */
    ROAD_NAME_ADDRESS_EMPTY_DETAIL_ADDRESS: 'ROAD_NAME_ADDRESS_EMPTY_DETAIL_ADDRESS',
}

/**
 * 메모 FE 검증 에러 코드
 * */
const MemoValidationErrorCode = {
    /** 메모 입력 초과 실패 */
    MEMO_MAX_FAIL: 'MEMO_MAX_FAIL',
    /** 메모 특수문자 입력 실패 */
    MEMO_INCLUDE_INVISIBLE_CHAR_FAIL: 'MEMO_INCLUDE_INVISIBLE_CHAR_FAIL',
    /** 수령위치를 선택해주세요. */
    PICKUP_LOCATION: 'PICKUP_LOCATION',
    /** 기타 수령 위치를 입력해 주세요. */
    PICKUP_LOCATION_GUIDE: 'PICKUP_LOCATION_GUIDE', // 이 에러 발생 시 시스템얼럿으로 처리하지 않고, 커스텀모달로 처리
    /** 수령 위치 입력 초과 실패 */
    PICKUP_LOCATION_NAME_MAX_FAIL: 'PICKUP_LOCATION_NAME_MAX_FAIL',
    /** 공동현관 비밀번호를 입력해 주세요. */
    COMMON_ENTRANCE_PASSWORD_GUIDE: 'COMMON_ENTRANCE_PASSWORD_GUIDE',
    /** 기타 출입 방법을 입력해 주세요. */
    ENTRY_METHOD_GUIDE: 'ENTRY_METHOD_GUIDE', // 이 에러 발생 시 시스템얼럿으로 처리하지 않고, 커스텀모달로 처리
    /** 출입 방법 입력 초과 실패 */
    ENTRY_METHOD_NAME_MAX_FAIL: 'ENTRY_METHOD_NAME_MAX_FAIL',
} as const

/** 배송지/메모 FE 검증 결과 코드 */
export const DeliveryValidationCode = {
    ...BaseAddressValidationErrorCode,
    ...BaseAddressValidationWarningCode,
    ...MemoValidationErrorCode,
} as const
export type DeliveryValidationCodeType = Constant<typeof DeliveryValidationCode>

/** 배송지 검증 오류/경고 메시지 */
export const DeliveryValidationMessages = {
    [DeliveryValidationCode.DELIVERY_INPUT_FAIL]: '배송지 정보를 입력해야 합니다.',
    [DeliveryValidationCode.DELIVERY_RECEIVER_NAME_FAIL]: '수령인을 입력해야 합니다.',
    [DeliveryValidationCode.DELIVERY_RECEIVER_NAME_MAX_FAIL]: `수령인은 최대 ${MaxLength.RECEIVER_NAME}자까지 입력 가능합니다.`,
    [DeliveryValidationCode.DELIVERY_ADDRESS_NAME_MAX_FAIL]: `배송지명은 최대 ${MaxLength.ADDRESS_NAME}자까지 입력 가능합니다.`,
    [DeliveryValidationCode.DELIVERY_DETAIL_ADDRESS_MAX_FAIL]: `상세주소는 최대 ${MaxLength.DETAIL_ADDRESS}자까지 입력 가능합니다.`,
    [DeliveryValidationCode.DELIVERY_PHONE_NUMBER_FAIL]:
        '연락처1이 부정확하거나, 지원하지 않는 국번을 사용중입니다. 연락처를 수정해주세요.',
    [DeliveryValidationCode.DELIVERY_PHONE_NUMBER_TWO_FAIL]:
        '연락처2이 부정확하거나, 지원하지 않는 국번을 사용중입니다. 연락처를 수정해주세요.',
    [DeliveryValidationCode.DELIVERY_KOREA_ADDRESS_FAIL]: '정확한 배송을 위해 배송지를 한글명으로 변경해주세요.',
    [DeliveryValidationCode.DELIVERY_ROAD_DETAIL_NONE]: '상세주소가 입력되지 않았습니다.',
    [DeliveryValidationCode.DELIVERY_LOT_DETAIL_NONE]: '상세주소를 입력해야 합니다.',
    [DeliveryValidationCode.DEPRECATED_ZIPCODES]:
        '선택하신 배송지 우편번호가 폐지되어 사용 불가합니다. 배송지를 수정해주세요.',
    [DeliveryValidationCode.REJECT_SIX_DIGITS_ZIPCODE_WITH_ARRIVAL_GUARANTEE_PRODUCT]:
        '선택한 주소는 N배송 상품 정보를 확인할 수 없습니다. 도로명 주소로 다시 입력해주세요.',
    [DeliveryValidationCode.OVERSEA_DELIVERY_NOT_ROAD_ADDRESS]:
        '해외직배송상품은 통관진행 및 원활한 배송처리를 위해 도로명주소+5자리 우편번호로 입력하셔야 합니다.\n배송지 주소를 도로명 주소로 수정해주세요.',
    [DeliveryValidationCode.REJECT_LOT_NUMBER_ADDRESS]:
        '우편번호 6자리 배송지는 사용이 불가합니다. 정확한 배송을 위해 우편번호 5자리 배송지로 수정해주세요.',
    /** `배송메모는 최대 ${MaxLength.DELIVERY_MEMO}자까지 입력 가능합니다.` */
    [DeliveryValidationCode.MEMO_MAX_FAIL]: DELIVERY_MEMO_ERROR_MESSAGE.MEMO_MAX_FAIL,
    /** '배송메모에 사용불가한 특수문자가 있습니다. 다시 입력해주세요.' */
    [DeliveryValidationCode.MEMO_INCLUDE_INVISIBLE_CHAR_FAIL]:
        DELIVERY_MEMO_ERROR_MESSAGE.MEMO_INCLUDE_INVISIBLE_CHAR_FAIL,
    [DeliveryValidationCode.PICKUP_LOCATION]: '정확한 배송을 위해 출입방법을 설정해주세요.',
    [DeliveryValidationCode.PICKUP_LOCATION_GUIDE]: '기타 수령 위치를 입력해 주세요.', // 커스텀모달로 처리
    [DeliveryValidationCode.PICKUP_LOCATION_NAME_MAX_FAIL]: `받을 곳은 최대 ${MaxLength.PICKUP_MEMO}자까지 입력 가능합니다.`,
    [DeliveryValidationCode.COMMON_ENTRANCE_PASSWORD_GUIDE]: '공동현관 인터폰 비밀번호를 입력해 주세요.', // 커스텀모달로 처리
    [DeliveryValidationCode.ENTRY_METHOD_GUIDE]: '공동현관 출입 방법을 입력해 주세요.', // 커스텀모달로 처리
    [DeliveryValidationCode.ENTRY_METHOD_NAME_MAX_FAIL]: `출입 방법은 최대 ${MaxLength.ENTRY_MEMO}자까지 입력 가능합니다.`,
    [DeliveryValidationCode.ROAD_NAME_ADDRESS_EMPTY_DETAIL_ADDRESS]:
        '상세주소가 입력되지 않았습니다. 그대로 주문하시겠습니까?',
} satisfies Record<DeliveryValidationCodeType, string>

/**
 * 주문서 > 배송지영역 Modal Case
 */

export const DeliveryModalCase = {
    /** 주문완료 > 배송메모 */
    DELIVERY_MEMO: 'DELIVERY_MEMO',
    /** 안심번호 서비스 안내 */
    VIRTUAL_PHONE_NUMBER_INFO: 'VIRTUAL_PHONE_NUMBER_INFO',
    /** 개인통관고유부호 본인 정보 불러오기 */
    CUSTOMS_CODE_CONSENT: 'CUSTOMS_CODE_CONSENT',
    /** 개인통관고유부호 통합 페이지 */
    PERSONAL_CUSTOMS_CLEARANCE_SCREEN: 'PERSONAL_CUSTOMS_CLEARANCE_SCREEN',
    /** 도착보장예정일 변경 안내 */
    ARRIVAL_GUARANTEE_DATE_CHANGED: 'ARRIVAL_GUARANTEE_DATE_CHANGED',
} as const

export type DeliveryModalCaseValue = Constant<typeof DeliveryModalCase>

type OmitDeliveryModalCaseValue = Constant<
    Omit<
        typeof DeliveryModalCase,
        typeof DeliveryModalCase.DELIVERY_MEMO | typeof DeliveryModalCase.ARRIVAL_GUARANTEE_DATE_CHANGED
    >
>

export type ModalCaseType = Maybe<
    | {
          case: typeof DeliveryModalCase.DELIVERY_MEMO
          options: {
              deliveryMemoSheetProductId?: string
          }
      }
    | {case: typeof DeliveryModalCase.ARRIVAL_GUARANTEE_DATE_CHANGED; dateChangedProductIds: string[]}
    | {case: OmitDeliveryModalCaseValue}
>

/**
 * 직접 수령하기를 허용하지 않는 장보기 마켓 리스트
 * 추후에 직접 수령하기 spec out 예정
 */
export const NotAllowDirectReceiveBranchList = [BranchMarket.KURLYNMART]
